"use server";

import { redirect } from "next/navigation";

import { authErrorMessage } from "./errors";
import { availableOAuthProviders } from "./oauth-availability";
import { oauthParams } from "./oauth-params";
import type { OAuthProvider } from "./oauth-providers";
import { requestOrigin } from "./origin";
import { withQuery } from "./error-return";
import { isShellRequest } from "@/lib/shell-server";
import { getServerSupabase } from "@/lib/supabase/server";

/** A provider the caller's client is not offered must not start a flow just
 *  because a Server Action is a POST endpoint anyone with a session can
 *  reach. Hiding the button is where guideline 4.8 is actually satisfied —
 *  4.8 governs what an app OFFERS — but the purchase-surface rule next door
 *  (lib/shell-guards.test.ts) learned the harder lesson the same review
 *  cycle taught us on 3.1.1: "not linked" is not the same as "not
 *  reachable", so the guard belongs in the action too.
 *
 *  Derived from `availableOAuthProviders`, never a second list: when Apple
 *  is added to SHELL_PROVIDERS, its action starts working in the shell with
 *  no change here. */
const UNAVAILABLE_PROVIDER_MESSAGE =
  "That sign-in option isn't available here. Please use your email and password.";

/** Trigger an OAuth flow from a Server Action.
 *  `errorReturnPath` is where to redirect on failure (with `?oauth_error`)
 *  — typically `/login` or `/signup` so the source form can render the
 *  message. `next` is the post-auth destination, threaded into the
 *  callback URL so the round-trip preserves the user's intent. On success
 *  this throws via `redirect(provider.url)`. */
export async function startOAuth(
  provider: OAuthProvider,
  errorReturnPath: string,
  next?: string | null,
): Promise<never> {
  const [supabase, origin, shell] = await Promise.all([
    getServerSupabase(),
    requestOrigin(),
    isShellRequest(),
  ]);

  if (!availableOAuthProviders(shell).includes(provider)) {
    redirect(
      `${withQuery(errorReturnPath)}oauth_error=${encodeURIComponent(UNAVAILABLE_PROVIDER_MESSAGE)}`,
    );
  }

  const { data, error } = await supabase.auth.signInWithOAuth(
    oauthParams(provider, origin, next, { shell }),
  );
  if (error || !data.url) {
    redirect(`${withQuery(errorReturnPath)}oauth_error=${encodeURIComponent(authErrorMessage(error))}`);
  }
  redirect(data.url);
}

/** Shell variant: returns the authorize URL as DATA instead of redirecting.
 *  In the native shell the webview must never main-frame-navigate to the
 *  off-host authorize URL — Capacitor policy-cancels it, which both fires
 *  the offline `errorPath` page (hijacking the webview mid-login) and
 *  leaves nothing listening for the deep-link return. OAuthForm
 *  main-frame `location.assign`s the returned URL instead (NOT window.open
 *  — WKWebView disables popups, so it silently returns null; see that file):
 *  Capacitor hands the navigation to the
 *  system browser while the webview stays parked with ShellBridge armed.
 *  The PKCE verifier cookie still lands in the webview's jar via this
 *  action's response. */
export async function startShellOAuth(
  provider: OAuthProvider,
  next?: string | null,
): Promise<{ url: string } | { error: string }> {
  const [supabase, origin] = await Promise.all([
    getServerSupabase(),
    requestOrigin(),
  ]);

  // Gated against the SHELL list specifically, not the caller's detected
  // client: this function unconditionally builds shell deep-link params, so
  // "is this provider offered in the shell?" is the only question that
  // matters regardless of who called it.
  if (!availableOAuthProviders(true).includes(provider)) {
    return { error: UNAVAILABLE_PROVIDER_MESSAGE };
  }

  const { data, error } = await supabase.auth.signInWithOAuth(
    oauthParams(provider, origin, next, { shell: true }),
  );
  if (error || !data.url) {
    return { error: authErrorMessage(error) };
  }
  return { url: data.url };
}
